<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { UiAside, UiText, UiIcon, UiButton, UiLink, UiTooltip } from '@shopexperts/shared';
import type { AdminExpertClaim } from '@shopexperts/legacy-api';
import ClaimStateChips from './ClaimStateChips.vue';
import ClaimNoteField from './ClaimNoteField.vue';
import ClaimActionButton from './ClaimActionButton.vue';
import { useExpertClaimActions } from '@/app/composables/expert-claims/useExpertClaimActions';
import { isClaimNoteOverLimit } from './claimAppearance';
import { formatAbsoluteTime } from '@/utils/date';

const props = defineProps<{
  claim: AdminExpertClaim | null;
}>();

const model = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  // Approve/reject carry the unsaved note draft (if any) so text typed in the
  // drawer is prefilled into the decision dialog instead of silently lost.
  approve: [draftNote?: string];
  reject: [draftNote?: string];
  cancelDecision: [];
  edit: [];
}>();

const { updateNote } = useExpertClaimActions();

// --- Guard reasons (mirror the API's 409 checks, in the API's check order) ---

const approveDisabledReason = computed<string | null>(() => {
  if (!props.claim) return null;
  if (props.claim.decision !== null) return 'Already decided';
  if (props.claim.status !== 'verified') return 'Not email-verified yet';
  if (props.claim.expert.is_claimed) return 'Profile already claimed';
  return null;
});

const rejectDisabledReason = computed<string | null>(() => {
  if (!props.claim) return null;
  return props.claim.decision !== null ? 'Already decided' : null;
});

const editDisabledReason = computed<string | null>(() => {
  if (!props.claim) return null;
  return props.claim.decision !== null ? 'Cancel the decision first' : null;
});

const cancelDisabledReason = computed<string | null>(() => {
  if (!props.claim) return null;
  return props.claim.decision === null ? 'No decision to cancel' : null;
});

// --- Review-state summary ---

const isAutoRejected = computed(
  () => props.claim?.decision === 'rejected' && props.claim?.decided_by === null,
);

const stateSummary = computed<{ text: string; classes: string } | null>(() => {
  const claim = props.claim;
  if (!claim) return null;

  if (claim.decision === 'approved') {
    return {
      text: `Approved${claim.decided_by ? ` by ${claim.decided_by.name}` : ''}${claim.decided_at ? ` on ${formatAbsoluteTime(claim.decided_at, 'datetime')}` : ''}.`,
      classes: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    };
  }
  if (claim.decision === 'rejected') {
    if (isAutoRejected.value) {
      return {
        text: `Auto-rejected${claim.decided_at ? ` on ${formatAbsoluteTime(claim.decided_at, 'datetime')}` : ''} because another claim for this profile was approved.`,
        classes: 'bg-rose-50 border-rose-200 text-rose-800',
      };
    }
    return {
      text: `Rejected${claim.decided_by ? ` by ${claim.decided_by.name}` : ''}${claim.decided_at ? ` on ${formatAbsoluteTime(claim.decided_at, 'datetime')}` : ''}.`,
      classes: 'bg-rose-50 border-rose-200 text-rose-800',
    };
  }
  if (claim.status !== 'verified') {
    return {
      text: 'Waiting for the claimant to verify their email. You can reject now; approving requires a verified email.',
      classes: 'bg-amber-50 border-amber-200 text-amber-800',
    };
  }
  if (claim.expert.is_claimed) {
    return {
      text: "This profile already has an owner, so this claim cannot be approved. You can reject it, or cancel the other claim's approval first.",
      classes: 'bg-rose-50 border-rose-200 text-rose-800',
    };
  }
  return {
    text: 'Email verified — this claim is ready for review.',
    classes: 'bg-sky-50 border-sky-200 text-sky-800',
  };
});

// --- Admin note editor (allowed in any decision state) ---

const noteDraft = ref('');

watch(
  () => [props.claim?.id, props.claim?.admin_note] as const,
  () => {
    noteDraft.value = props.claim?.admin_note ?? '';
  },
  { immediate: true },
);

const isNoteOverLimit = computed(() => isClaimNoteOverLimit(noteDraft.value));
const noteChanged = computed(() => (props.claim?.admin_note ?? '') !== noteDraft.value);
const isNoteSaving = computed(() => updateNote.isPending.value);
/** Unsaved draft handed to the approve/reject dialogs so it isn't lost. */
const unsavedNoteDraft = computed(() =>
  noteChanged.value && noteDraft.value.trim() ? noteDraft.value.trim() : undefined,
);

const noteSavedRecently = ref(false);
let noteSavedTimer: ReturnType<typeof setTimeout> | null = null;

function flashNoteSaved() {
  noteSavedRecently.value = true;
  if (noteSavedTimer) clearTimeout(noteSavedTimer);
  noteSavedTimer = setTimeout(() => {
    noteSavedRecently.value = false;
  }, 2500);
}

onBeforeUnmount(() => {
  if (noteSavedTimer) clearTimeout(noteSavedTimer);
});

async function saveNote() {
  if (!props.claim || !noteChanged.value || isNoteOverLimit.value) return;
  const trimmed = noteDraft.value.trim();
  // `admin_note` is present + nullable on the API: empty clears to null.
  await updateNote.mutateAsync({
    claimId: props.claim.id,
    adminNote: trimmed || null,
  });
  flashNoteSaved();
}

async function clearNote() {
  if (!props.claim) return;
  await updateNote.mutateAsync({ claimId: props.claim.id, adminNote: null });
  noteDraft.value = '';
  flashNoteSaved();
}

// --- Display helpers ---

const claimantRows = computed<Array<{ label: string; value: string }>>(() => {
  const claim = props.claim;
  if (!claim) return [];
  if (claim.type === 'freelancer') {
    return [
      { label: 'First name', value: claim.first_name ?? '—' },
      { label: 'Last name', value: claim.last_name ?? '—' },
      { label: 'Role', value: claim.role ?? '—' },
    ];
  }
  return [
    { label: 'Full name', value: claim.full_name ?? '—' },
    { label: 'Company name', value: claim.company_name ?? '—' },
    { label: 'Company email', value: claim.company_email ?? '—' },
  ];
});

</script>

<template>
  <UiAside v-model="model" position="right">
    <div v-if="claim" class="flex flex-col h-full">
      <!-- Header -->
      <div class="p-6 border-b border-gray-100">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <UiText as="p" variant="caption-normal" class="text-gray-400" :text="`Claim #${claim.id}`" />
            <UiText
              as="h2"
              variant="h3"
              :text="claim.claimant_name || claim.email"
              class="text-gray-900 mt-0.5"
              :truncate="1"
            />
          </div>
          <button
            class="p-1.5 rounded-md hover:bg-gray-100 transition-colors shrink-0"
            aria-label="Close claim details"
            @click="model = false"
          >
            <UiIcon name="close" class="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <ClaimStateChips :claim="claim" class="mt-3" />
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto p-6 space-y-7">
        <!-- Review-state summary -->
        <div
          v-if="stateSummary"
          class="border rounded-lg px-4 py-3"
          :class="stateSummary.classes"
        >
          <UiText as="p" variant="p-small" :text="stateSummary.text" />
        </div>

        <!-- Claimant -->
        <section>
          <UiText as="h3" variant="caption" class="text-gray-500 tracking-wide block mb-3" text="Claimant" />
          <div class="grid grid-cols-2 gap-x-4 gap-y-4">
            <div v-for="row in claimantRows" :key="row.label" class="min-w-0">
              <UiText as="p" variant="caption-normal" class="text-gray-400" :text="row.label" />
              <UiText as="p" variant="p-small" class="text-gray-800 break-words" :text="row.value" />
            </div>
            <div class="min-w-0">
              <UiText as="p" variant="caption-normal" class="text-gray-400" text="Email" />
              <UiText as="p" variant="p-small" class="text-gray-800 break-all" :text="claim.email" />
            </div>
            <div class="col-span-2 min-w-0">
              <UiText as="p" variant="caption-normal" class="text-gray-400" text="LinkedIn" />
              <UiLink
                :href="claim.linkedin_url"
                :label="claim.linkedin_url"
                variant="p-small"
                external="with-arrow"
                overflow-ellipsis
                class="max-w-full"
              />
            </div>
          </div>
        </section>

        <!-- Claimed profile -->
        <section>
          <UiText as="h3" variant="caption" class="text-gray-500 tracking-wide block mb-3" text="Claimed profile" />
          <div class="border border-gray-100 rounded-lg p-4">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <UiText
                  as="p"
                  variant="p-small"
                  class="text-gray-900 font-medium truncate"
                  :text="claim.expert.name || claim.expert.slug"
                />
                <UiText
                  as="p"
                  variant="caption-normal"
                  class="text-gray-400 truncate"
                  :text="`/${claim.expert.slug} · ${claim.expert.type === 'agency' ? 'Agency' : 'Freelancer'}`"
                />
              </div>
              <UiTooltip
                v-if="claim.expert.is_claimed"
                :content="claim.decision === null
                  ? 'This profile already has an owner — this claim cannot be approved'
                  : 'This profile has an owner'"
              >
                <span
                  class="inline-flex items-center gap-1 shrink-0"
                  :class="claim.decision === null ? 'text-rose-600' : 'text-gray-400'"
                >
                  <UiIcon name="lock" class="w-3.5 h-3.5" />
                  <UiText as="span" variant="caption-normal" text="claimed" />
                </span>
              </UiTooltip>
            </div>
            <UiLink
              :href="`/admin/expert-manager/${claim.expert.id}`"
              label="Open in Expert Manager"
              variant="p-small"
              external="none"
              class="mt-2 inline-block"
            />
          </div>
        </section>

        <!-- Decision -->
        <section>
          <UiText as="h3" variant="caption" class="text-gray-500 tracking-wide block mb-3" text="Decision" />
          <div class="grid grid-cols-2 gap-x-4 gap-y-4">
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400" text="Decided by" />
              <UiText
                as="p"
                variant="p-small"
                class="text-gray-800"
                :text="claim.decided_by?.name ?? (isAutoRejected ? 'System (auto-rejected)' : '—')"
              />
            </div>
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400" text="Decided at" />
              <UiText
                as="p"
                variant="p-small"
                class="text-gray-800"
                :text="claim.decided_at ? formatAbsoluteTime(claim.decided_at, 'datetime') : '—'"
              />
            </div>
            <div v-if="claim.created_user" class="col-span-2">
              <UiText as="p" variant="caption-normal" class="text-gray-400" text="Account" />
              <span class="inline-flex items-center gap-1.5 mt-0.5 px-2 py-0.5 rounded bg-gray-50 border border-gray-200">
                <UiIcon name="user-circle" class="w-3.5 h-3.5 text-gray-400" />
                <UiText
                  as="span"
                  variant="caption-normal"
                  class="text-gray-700"
                  :text="`User #${claim.created_user.id} · ${claim.created_user.email}`"
                />
              </span>
            </div>
          </div>
        </section>

        <!-- Admin note -->
        <section>
          <UiText as="h3" variant="caption" class="text-gray-500 tracking-wide block mb-3" text="Admin note" />
          <ClaimNoteField
            v-model="noteDraft"
            placeholder="Add an internal note about this claim..."
            :disabled="isNoteSaving"
          />
          <div class="flex items-center gap-2">
            <UiButton
              label="Save note"
              variant="outline"
              size="sm"
              :disabled="!noteChanged || isNoteOverLimit || isNoteSaving"
              @click="saveNote"
            />
            <UiButton
              v-if="claim.admin_note !== null"
              label="Clear note"
              variant="ghost"
              size="sm"
              :disabled="isNoteSaving"
              @click="clearNote"
            />
            <UiText
              v-if="noteChanged && !isNoteSaving"
              as="span"
              variant="caption-normal"
              class="text-amber-600"
              text="Unsaved changes"
            />
            <UiText
              v-else-if="noteSavedRecently"
              as="span"
              variant="caption-normal"
              class="text-emerald-600"
              text="Saved"
            />
          </div>
        </section>

        <!-- Timestamps -->
        <section>
          <UiText as="h3" variant="caption" class="text-gray-500 tracking-wide block mb-3" text="Timeline" />
          <div class="grid grid-cols-2 gap-x-4 gap-y-4">
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400" text="Submitted" />
              <UiText
                as="p"
                variant="p-small"
                class="text-gray-800"
                :text="formatAbsoluteTime(claim.created_at, 'datetime')"
              />
            </div>
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400" text="Email verified" />
              <UiText
                as="p"
                variant="p-small"
                class="text-gray-800"
                :text="claim.verified_at ? formatAbsoluteTime(claim.verified_at, 'datetime') : 'Not verified'"
              />
            </div>
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400" text="Last updated" />
              <UiText
                as="p"
                variant="p-small"
                class="text-gray-800"
                :text="formatAbsoluteTime(claim.updated_at, 'datetime')"
              />
            </div>
          </div>
        </section>
      </div>

      <!-- Actions -->
      <div class="p-6 border-t border-gray-100">
        <div class="grid grid-cols-2 gap-2">
          <ClaimActionButton
            label="Approve"
            variant="primary"
            :disabled-reason="approveDisabledReason"
            @click="emit('approve', unsavedNoteDraft)"
          />
          <ClaimActionButton
            label="Reject"
            variant="danger"
            :disabled-reason="rejectDisabledReason"
            @click="emit('reject', unsavedNoteDraft)"
          />
          <ClaimActionButton
            label="Edit claim"
            variant="outline"
            :disabled-reason="editDisabledReason"
            @click="emit('edit')"
          />
          <ClaimActionButton
            label="Cancel decision"
            variant="outline"
            :disabled-reason="cancelDisabledReason"
            @click="emit('cancelDecision')"
          />
        </div>
      </div>
    </div>
  </UiAside>
</template>
